import React, {createContext, PropsWithChildren, useContext} from 'react';
import {Environment} from './environment';
import {getLanguage, Locale} from './locale';
import {HostConfig} from './urlMappings/types';

export type StaticHostConfig = HostConfig<Locale, Environment>;

const ConfigContext = createContext<StaticHostConfig>(
  new Proxy<StaticHostConfig>({} as StaticHostConfig, {
    get() {
      throw new Error(
        'Cannot find value for ConfigContext. Did you add a ConfigProvider in one of the parent components?',
      );
    },
  }),
);

export const useConfig = (): StaticHostConfig => useContext(ConfigContext);
export const useLegacyConfig = () => useConfig().legacyConfig;

export const useShopId = () => useConfig().legacyConfig.shopId;
export const useChildShopId = () => useConfig().legacyConfig.childShopId;

export const useHostName = () => {
  const config = useConfig();

  return 'publicDesktopHostname' in config
    ? (config.publicDesktopHostname as string)
    : config.hostname;
};
export const usePublicProtocol = () => useConfig().publicProtocol;
export const usePublicPort = () => useConfig().publicPort;

export const useAlternateLocales = () =>
  useContext(ConfigContext).alternateLocales;

export const useCurrentNativeLanguage = () =>
  useContext(ConfigContext).nativeLanguage;

export const useCurrentLanguageCode = () => {
  const locale = useConfig().locale;
  return getLanguage(locale);
};

export const useCurrentDomainName = () => {
  const hostname = useConfig().hostname;
  return hostname.substring(hostname.indexOf('.') + 1);
};

export const useCheckoutFrontendHost = () => {
  return useConfig().checkoutFrontendHost;
};

export const useCheckoutShopsApiUrl = () => {
  return useConfig().checkoutShopsApiUrl;
};

export const useCheckoutTrackingUrl = () => {
  return useConfig().checkoutTrackingUrl;
};

export const useCheckoutAppUrl = () => {
  return useConfig().checkoutAppUrl;
};

export const useCmsUrl = () => {
  return useConfig().cmsUrl;
};

export function ConfigProvider({
  config,
  children,
}: PropsWithChildren<{config: StaticHostConfig}>) {
  return (
    <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
  );
}
